/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import writeXlsxFile, {Cell, Columns, Row, SheetData} from 'write-excel-file';

interface DownloadReferenceField {
  id?: string;
  apiId?: string;
  altId?: string;
  isDate?: boolean;
  allowDecimals?: boolean;
}

export interface XlsxExport {
  data: SheetData;
  columns: Columns;
  fileName: string;
}

function precision(value: number): number {
  if (!isFinite(value)) {
    return 0;
  }
  let exponent = 1;
  let decimalPlaces = 0;
  while (Math.round(value * exponent) / exponent !== value) {
    exponent *= 10;
    decimalPlaces++;
  }
  return decimalPlaces;
}

function buildDataCell(
  value: any,
  column: string,
  referenceField: DownloadReferenceField | undefined,
  treatDateAsDate: boolean
): Cell {
  const stringFieldIds = ['name', 'labels'];
  const columnLooksLikeDate = ['date', 'time', 'chunk'].some(part => _.lowerCase(column).includes(part));
  const isDate = !!referenceField?.isDate || (treatDateAsDate && column === 'date') ||
    ((_.isString(value) || _.isDate(value)) && moment(value, true).isValid() && columnLooksLikeDate);
  if (isDate) {
    const parsedValue = value && moment(value, true).isValid()
      ? moment.tz(moment(value).format('YYYY-MM-DD HH:mm:ss'), 'UTC').toDate()
      : undefined;
    return {
      type: Date,
      format: 'yyyy mmm dd HH:mm:ss',
      value: parsedValue
    };
  }

  const isNumber = (referenceField && _.isBoolean(referenceField.allowDecimals)) ||
    ((!referenceField || !stringFieldIds.includes(referenceField.id || '')) &&
      (_.isFinite(value) || (_.isString(value) && value === parseFloat(value).toString())));
  if (isNumber) {
    const managedValue = (_.isString(value) ? parseFloat(value) :
      (_.isFinite(_.get(value, 'amount')) ? value.amount : value)) as number;
    const decimalPlaces = Math.min(precision(managedValue), 6);
    return {
      type: Number,
      format: decimalPlaces === 0 ? '0' : '0.' + '0'.repeat(decimalPlaces),
      value: managedValue
    };
  }

  return {
    type: String,
    value: (_.isFinite(value) ? '' : value) as string
  };
}

export function buildXlsxExport(
  title: string,
  headingFields?: string[] | null,
  dataFields?: string[] | null,
  dataRows?: Array<Record<string, any>> | null,
  referenceFields?: DownloadReferenceField[] | Record<string, DownloadReferenceField>,
  treatDateAsDate = false
): XlsxExport {
  const fields = _.toArray(referenceFields);
  const headings = headingFields || [];
  const columnsToExport = dataFields || [];
  const rows = dataRows || [];
  const actualData: SheetData = rows.map(dataRow => columnsToExport.map(column => {
    const referenceField = fields.find(field =>
      field.apiId === column || field.altId === column || field.id === column
    );
    const value = dataRow[column + 'Start'] || _.get(dataRow, [column, 'count']) || dataRow[column];
    return buildDataCell(value, column, referenceField, treatDateAsDate);
  }));

  const headerRow: Row = headings.map((heading, index) => ({
    value: heading,
    fontWeight: 'bold',
    align: actualData[0]?.[index]?.type === String ? 'left' : 'right'
  }));
  const columns: Columns = headings.map((heading, index) =>
    actualData[0]?.[index]?.type === Date ? {width: 20} : {}
  );

  return {
    data: [headerRow, ...actualData],
    columns,
    fileName: _.endsWith(title, '.xls') ? title + 'x' : title
  };
}

export class Downloader {
  saveXls2(
    title: string,
    headingFields?: string[] | null,
    dataFields?: string[] | null,
    dataRows?: Array<Record<string, any>> | null,
    referenceFields?: DownloadReferenceField[] | Record<string, DownloadReferenceField>,
    treatDateAsDate = false
  ): Promise<void> {
    const xlsxExport = buildXlsxExport(
      title,
      headingFields,
      dataFields,
      dataRows,
      referenceFields,
      treatDateAsDate
    );
    return writeXlsxFile(xlsxExport.data, {
      columns: xlsxExport.columns,
      fileName: xlsxExport.fileName
    });
  }
}
